import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Image from 'next/image';
import Link from 'next/link';
import { ENDPOINTS, WEB_URL } from '@/config/endpoints';
import { ThreeDots } from 'react-loader-spinner';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getMetadata } from "@/component/shared/getMetaData";
import enIN from '@/locales/blogs/enIN';
import enUS from '@/locales/blogs/enUS';
import esMX from '@/locales/blogs/esMX';
import ArticlesCategories from '@/component/shared/articles/articlesCategories';

const Pagination = dynamic(() => import('@/component/shared/pagination'), {
    loading: () => <p>Loading...</p>});
const ResourcesLinks = dynamic(() => import('@/layout/resourcesLinks'), { ssr: false });

const Articles=()=> {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loader,setLoader] = useState(true);
  const router = useRouter();
  const { locale } = router;
  const t = (locale === 'es-mx' && esMX) || (locale === 'en-us' && enUS) || (locale === 'en-in' && enIN);
  const metadata = getMetadata(t);

    useEffect(() => {
    const fetchPosts = async () => {
      try{
        const res = await axios.get(locale == 'en-in' ? `${ENDPOINTS.BLOG_INDUSTRY_POST}&page=${currentPage}&size=12` : `${ENDPOINTS.SPANISH_POST}&page=${currentPage}&size=12`);
        setPosts(res?.data?.data?.content);
        setLoader(false);
        setTotalPages(res?.data?.data?.totalPages);
      } catch (error) {
        console.error('Error:', error);
        // setPosts([]);
        // setLoader(false);
      }
    };
    fetchPosts();
  }, [currentPage,locale]);

  const handleNextPage = () => {
    setLoader(true);
    if (currentPage < totalPages) {
      setLoader(false);
      const nextPage = currentPage + 1;
      setCurrentPage(currentPage + 1);
      updatePageQuery(nextPage);
    }
  };

  const handlePrevPage = () => {
    setLoader(true);
    if (currentPage > 1) {
      setLoader(false);
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      updatePageQuery(prevPage);
    }
  };
  const handlePageClick = (page) => {
    setCurrentPage(page);
    updatePageQuery(page);
  };
  const updatePageQuery = (page) => {
    router.push({
      pathname: router.pathname,
      query: { ...router.query, page: page.toString() },
    });
  };
  useEffect(() => {
    if (router.query.page) {
      setCurrentPage(parseInt(router.query.page));
    }
  }, [router.query.page]);


  const dateFormat = (publishDate)=>{
    const dateString = new Date(publishDate);
    const options = { day:'numeric', year: 'numeric', month: 'long' };
    if (typeof dateString === 'object' && dateString !== null && 'toLocaleDateString' in dateString) {
      const result = dateString.toLocaleDateString('en-US',options);
      return <span>{result}</span>
    } 
  }
  const dateFormatSpanish = (publishDate)=>{
    const dateString = new Date(publishDate);
    const options = { day:'numeric', year: 'numeric', month: 'long' };
    if (typeof dateString === 'object' && dateString !== null && 'toLocaleDateString' in dateString) {
      const result = dateString.toLocaleDateString('es-MX',options);
      return <span>{result}</span>
    }
  }
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageDisplay = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPageDisplay / 2));
    let endPage = startPage + maxPageDisplay - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPageDisplay + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={currentPage === i ? 'active' : ''}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };

  const truncateString=(str, maxLength)=> {
    if (str.length <= maxLength) {
      return str; // No truncation needed
    }
  
    const truncatedString = str.slice(0, maxLength - 3) + '...';
    return truncatedString;
  }
  const truncatedTitle=(title)=>{
    const originalString = title;
    const maxLength = 50;
    return truncateString(originalString, maxLength);
  }

  if (!posts) {
    return <div className='fixed inset-0 z-[9999] items-center flex bg-[rgba(255,255,255,0.8)]'>
    <div className='w-[100px] mx-auto h-[100px] justify-center flex'>
    <ThreeDots 
      height="80" 
      width="80" 
      radius="9"
      color="#032B5C" 
      ariaLabel="three-dots-loading"
      wrapperStyle={{}}
      wrapperClassName="m-auto"
      visible={true}
      />
      </div>
    </div>;
  }

  const toTitleCase = (str) => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const firstImageUrl = posts[0]?.featureImage || null;

  return (
    <main className='mt-[100px]'>
      <Head>  
          <title>{metadata.title}</title>
          <meta name="description" content={metadata.description} />
          <meta name="keywords" content={metadata.keywords} />
          {/* OpenGraph tags */} 
          <meta property="og:title" content={metadata.openGraph.title} />
          <meta property="og:description" content={metadata.openGraph.description} />
          <meta property="og:url" content={metadata.openGraph.url} />
          <meta property="og:image" content={metadata.openGraph.images} />
          <link rel="canonical" href={metadata.alternates.canonical} />
          
       {firstImageUrl && (
           <link
             rel="preload"
             as="image"
             href={firstImageUrl}
           />
        )}
        </Head>
        
      <section className='pt-20 pb-20 bg-white sm:pb-2 sm:pt-10'>
          <div className='container  lg:px-3  px-3 xm:px-2'>
            <ResourcesLinks />

            <div className='flex flex-wrap flex-1'>
              {locale == 'en-in' &&
              <div className="w-[25%] md:w-full sm:w-full md:mt-4 sm:mt-0 sm:pr-0 pr-1 pt-2">
                <div className="lg:sticky xm:sticky lg:top-[135px] xm:top-[100px]">
                    <ArticlesCategories/>
                </div>
              </div>
              }
           {locale == 'es-mx' &&
              <div className="w-[25%] md:w-full sm:w-full md:mt-4 sm:mt-0 sm:pr-0 pr-1 pt-2">
                <div className="lg:sticky xm:sticky lg:top-[135px] xm:top-[100px]">
                    <ArticlesCategories/>
                </div>
              </div>
              }
             {/* {posts.length === 0 && 
                <div className="text-center py-10">
                  <h2 className="text-2xl font-bold mb-4">{locale === 'es-mx' ? "Entrada del blog próximamente." : "Blog post coming soon."}</h2>
                </div>
              } */}
              <div className='w-[75%] min-h-screen  md:w-full sm:w-full'>
                <ul className="flex flex-wrap flex-1 -mx-xsp">
                  {posts.map((post,i) => (
                    <li key={i} className='w-1/3 md:w-1/2 sm:w-full py-2 px-1 list-none'>
                      <div className="bg-white shadow-cardShadow2 border border-[#E6E6E6] min-h-[300px] hover:shadow-cardHover2">
                      <Link href={WEB_URL+`${locale == 'es-mx'? '/es-mx/blogs/':'/articles/'}${post.slug}`}>
                      {post.featureImage&& (
                          <Image className='w-full sm:h-[209px] object-cover' priority={i === 0 ?true:false}
                            src={post.featureImage} fetchPriority={i === 0 ?'high':'auto'}
                            alt={post.title} width={640} height={427}
                          />
                        )}
                      </Link>
                      
                        <div className="px-2 pt-3 sm:min-h-[150px]">
                              <Link href={WEB_URL+`${locale == 'es-mx'? '/es-mx/blogs/':'/articles/'}${post.slug}`}>
                              <p className='text-xs text-[#7b7b7b]'>
                                {locale == 'es-mx' ? dateFormatSpanish(post.publishedDate) :  dateFormat(post.publishedDate)}
                                </p>
                              <h3 className="text-sm text-secondary my-1 mb-3 min-h-6 break-words" dangerouslySetInnerHTML={{ __html: truncatedTitle(post.title) }} />
                              <div className='flex items-end justify-between mb-1'>
                              <button className="flex item-center text-brand text-xs">
                              {locale == 'en-in' ? 'Read More' :'Leer más'}
                                  <span className="ml-1">
                                  <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" fill="rgba(2,43,92,1)"></path>
                                  </svg>
                                  </span>
                                </button>
                              </div>
                              </Link>
                              {locale == 'en-in' &&
                              <div className='mb-3'>
                                {
                                post.categories.map(category => (
                                  <Link key={category.createdAt} className='text-[12px] text-brand bg-[#ddd] rounded-md py-0.5 px-xsp mr-1 hover:underline' href={WEB_URL+`/articles/category/${category.slug}`}>{toTitleCase(category.name)}</Link>
                                ))}
                              </div>
                            }
                          </div>                              
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
        {!loader && totalPages > 1 &&
        <Pagination pageNumbers={renderPageNumbers()} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} currentPage={currentPage} totalPages={totalPages}/>
        }
        {loader &&
        <div className='fixed inset-0 z-[9999] items-center flex bg-[rgba(255,255,255,0.8)]'>
          <div className='w-[100px] mx-auto h-[100px] justify-center flex'>
          <ThreeDots 
            height="80" 
            width="80" 
            radius="9"
            color="#032B5C" 
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName="m-auto"
            visible={true}
            />
            </div>
          </div>
      }
    </main>
  );
}

export default Articles;
